// The four documents theatre actually puts on paper.
//
// Until now every "Print" button in OT called window.print(), which prints the
// browser's view of the page — sidebar, navigation, buttons and all — on
// whatever paper happens to be loaded. A call slip that comes out with a
// navigation menu down one side is not a document a porter can act on, and a
// consent form is not something you print out of a viewport at all.
//
//   Call slip       — the porter's instruction: who to fetch, from where, to
//                     which theatre, for what, and who asked.
//   Final list      — the day's authorised list, one line per case, with the
//                     allergy and bio-hazard lines that are the whole reason a
//                     paper list still exists.
//   Operative record— the merged note: header, the three notes, counts and
//                     what was used. The signed account of the operation.
//   Consent form    — the blank form, pre-filled with the patient and the
//                     procedure, for the patient to sign. There is no way to
//                     print a signed consent the system does not hold; what a
//                     theatre needs is the form with the right details on it.
//
// The layout primitives come from the inventory module's pdfTheme, deliberately.
// It is the house PDF style — letterhead, cards, tables, footers — and a second
// one invented here would make an OT document look like it came from a
// different hospital than the purchase order.

import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

import {
  C,
  SIDE_M,
  U,
  CARD_W,
  CARD_X_RIGHT,
  CARD_GAP,
  CARD_PAD_X,
  CONT_TOP,
  DASH,
  displayOrDash,
  drawCardFrame,
  drawContinuationHeader,
  drawLetterhead,
  drawLines,
  drawRowRule,
  drawRows,
  drawSignatureRow,
  drawTitleRow,
  ensureSpace,
  measureLines,
  measureRows,
  stampFooters,
  tableStyles,
} from "../../inventory/utils/pdfTheme";
import {
  formatRegistrationLine,
  resolveFacilityIdentity,
} from "../../inventory/utils/facilityIdentity";
import { formatIstDate, formatIstDateTime, formatIstTime } from "../../../utils/istDateTime";

const NAVY = C.navy;

/** Everything every OT document opens with. Returns the doc and the y to build from. */
function startDoc({ title, subtitle, chipLabel, chipColor = NAVY }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const identity = resolveFacilityIdentity();
  const y = drawLetterhead(doc, identity, formatRegistrationLine(identity), null, true);
  return {
    doc,
    identity,
    y: drawTitleRow(doc, y, { title, subtitle, chipLabel, chipColor }),
  };
}

/** A label/value card, measured and drawn in one call. @returns {number} its bottom y */
function card(doc, x, y, w, title, rows) {
  const { counts, height } = measureRows(doc, rows, w);
  const bodyTop = drawCardFrame(doc, x, y, w, height, title);
  drawRows(doc, x, bodyTop, w, rows, counts);
  return y + height;
}

/** A prose card. @returns {number} its bottom y */
function textCard(doc, x, y, w, title, text) {
  const { lines, height, pitch } = measureLines(doc, text || DASH, w);
  const bodyTop = drawCardFrame(doc, x, y, w, height, title);
  drawLines(doc, x, bodyTop, lines, pitch);
  return y + height;
}

/** Two cards side by side. @returns {number} the bottom of the taller one */
function cardPair(doc, y, left, right) {
  const leftRows = measureRows(doc, left.rows, CARD_W);
  const rightRows = measureRows(doc, right.rows, CARD_W);
  const height = Math.max(leftRows.height, rightRows.height);

  let top = drawCardFrame(doc, SIDE_M, y, CARD_W, height, left.title);
  drawRows(doc, SIDE_M, top, CARD_W, left.rows, leftRows.counts);
  top = drawCardFrame(doc, CARD_X_RIGHT, y, CARD_W, height, right.title);
  drawRows(doc, CARD_X_RIGHT, top, CARD_W, right.rows, rightRows.counts);

  return y + height;
}

function open(doc) {
  const url = URL.createObjectURL(doc.output("blob"));
  window.open(`${url}#zoom=page-fit`, "_blank", "noopener");
}

// ── 1. Call slip ──────────────────────────────────────────────────────────────

/**
 * The porter's instruction, on one sheet.
 *
 * A WARNING BAND, not a card, carries the allergy and the bio-hazard: those two
 * facts are why the slip travels with the patient, and a porter reading it in a
 * corridor must not have to find them among ten other rows.
 */
export function printCallSlip(slip, otCase = {}) {
  const { doc, identity, y } = startDoc({
    title: "Theatre Call Slip",
    subtitle: `Issued ${formatIstDateTime(slip.issued_at) || DASH} by ${
      slip.issued_by_name || DASH
    }`,
    chipLabel: (slip.porter_task_status || "ISSUED").replace(/_/g, " "),
  });

  let next = cardPair(doc, y, {
    title: "Patient",
    rows: [
      ["Name", slip.patient_name],
      ["MRN", slip.health_record_id],
      ["Age / sex", [slip.age ?? otCase.age, slip.gender || otCase.gender].filter(Boolean).join(" / ")],
      ["Ward / bed", `${slip.ward_name || DASH} / ${slip.bed_label || DASH}`],
    ],
  }, {
    title: "Theatre",
    rows: [
      ["Theatre", slip.operating_room_name],
      ["Scheduled", formatIstDateTime(slip.scheduled_start)],
      ["Procedure", slip.procedure_name],
      ["Diagnosis", slip.diagnosis || otCase.diagnosis],
      ["Surgeon", otCase.primary_surgeon_name],
    ],
  });

  // The two facts that must not be missed.
  const warnings = [
    otCase.known_allergies && `ALLERGY: ${otCase.known_allergies}`,
    otCase.biohazard &&
      `BIO-HAZARD${otCase.biohazard_note ? `: ${otCase.biohazard_note}` : ""}`,
  ].filter(Boolean);

  if (warnings.length) {
    next += CARD_GAP;
    const boxH = 6 + warnings.length * 5;
    doc.setDrawColor(...C.navy);
    doc.setLineWidth(0.6);
    doc.rect(SIDE_M, next, U, boxH, "S");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...C.navy);
    warnings.forEach((w, i) => {
      doc.text(w, SIDE_M + CARD_PAD_X, next + 6.5 + i * 5);
    });
    next += boxH;
  }

  const slipNotes = [slip.porter_note, slip.special_instructions]
    .filter(Boolean)
    .join(" · ");
  if (slipNotes) {
    next = textCard(doc, SIDE_M, next + CARD_GAP, U, "Notes for the porter", slipNotes);
  }

  // Signed on collection and on arrival — the slip is also the handover record.
  drawSignatureRow(doc, next + 6, "Ward nurse — patient handed over", "Theatre — patient received");

  stampFooters(doc, identity, true);
  open(doc);
}

// ── 2. Final list ─────────────────────────────────────────────────────────────

/**
 * The day's operating list.
 *
 * One row per case, ordered as the day runs. The allergy and bio-hazard go in a
 * second line under the patient rather than a column of their own, because they
 * are absent on most rows and a mostly-empty column reads as "nothing to see".
 */
export function printFinalList(dateIso, data) {
  const cases = data?.cases || [];
  const day = moment(dateIso, "YYYY-MM-DD").format("dddd, D MMMM YYYY");

  const { doc, identity, y } = startDoc({
    title: "Final OT List",
    subtitle: day,
    chipLabel: data?.authorised_only ? "AUTHORISED ONLY" : "ALL BOOKED",
  });

  doc.autoTable({
    ...tableStyles(),
    startY: y + 2,
    margin: { left: SIDE_M, right: SIDE_M, top: CONT_TOP },
    head: [["Time", "Theatre", "Patient", "Procedure", "Surgeon / anaesthetist", "Status"]],
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 24 },
      2: { cellWidth: 42 },
      3: { cellWidth: 44 },
      4: { cellWidth: 38 },
      5: { cellWidth: 22 },
    },
    body: cases.map((c) => [
      `${formatIstTime(c.start_datetime)}\n${formatIstTime(c.end_datetime)}`,
      displayOrDash(c.operating_room_name),
      [
        `${c.patient_name || DASH}`,
        [c.health_record_id, c.age != null ? `${c.age}${c.gender ? `/${c.gender}` : ""}` : null]
          .filter(Boolean)
          .join(" · "),
        c.ward_name || "",
        c.known_allergies ? `ALLERGY: ${c.known_allergies}` : "",
        c.biohazard ? `BIO-HAZARD${c.biohazard_note ? `: ${c.biohazard_note}` : ""}` : "",
      ]
        .filter(Boolean)
        .join("\n"),
      [
        c.procedure_name || DASH,
        c.diagnosis ? `Dx: ${c.diagnosis}` : "",
        c.special_instructions || "",
      ]
        .filter(Boolean)
        .join("\n"),
      [c.primary_surgeon_name || DASH, c.anaesthetist_name || DASH].join("\n"),
      [
        c.lifecycle_status || DASH,
        c.scheduling_type || "",
        c.reschedule_count > 0 ? `MOVED ${c.reschedule_count}x` : "",
      ]
        .filter(Boolean)
        .join("\n"),
    ]),
    didDrawCell: (d) => {
      if (d.column.index === 0) {
        drawRowRule(doc, d, SIDE_M, SIDE_M + U);
      }
    },
    didDrawPage: (d) => {
      if (d.pageNumber > 1) {
        drawContinuationHeader(doc, identity, "Final OT List", day, null, true);
      }
    },
  });

  const endY = doc.lastAutoTable?.finalY || y;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...C.muted);
  doc.text(
    `${cases.length} case(s).  Printed ${formatIstDateTime(new Date().toISOString())}.`,
    SIDE_M,
    endY + 6,
  );

  drawSignatureRow(doc, endY + 8, "OT manager", "Anaesthetist in charge");

  stampFooters(doc, identity, true);
  open(doc);
}

// ── 3. Operative record ───────────────────────────────────────────────────────

/** The note fields worth printing, per kind, in reading order. */
const RECORD_SECTIONS = {
  surgical: [
    ["pre_op_diagnosis", "Pre-operative diagnosis"],
    ["post_op_diagnosis", "Post-operative diagnosis"],
    ["procedure_performed", "Procedure performed"],
    ["operative_findings", "Findings"],
    ["procedure_details", "Details"],
    ["specimens", "Specimens"],
    ["implants_used", "Implants"],
    ["estimated_blood_loss", "Estimated blood loss"],
    ["complications", "Complications"],
    ["closure_details", "Closure"],
    ["drains", "Drains"],
    ["wound_class", "Wound class"],
    ["post_op_instructions", "Post-operative instructions"],
    ["vte_prophylaxis", "VTE prophylaxis"],
  ],
  anaesthesia: [
    ["asa_class", "ASA class"],
    ["anaesthesia_technique", "Technique"],
    ["airway_management", "Airway"],
    ["estimated_blood_loss", "Estimated blood loss"],
    ["fluids_given", "Fluids"],
    ["blood_products", "Blood products"],
    ["complications", "Complications"],
    ["lowest_temperature", "Lowest temperature"],
    ["recovery_instructions", "Recovery instructions"],
  ],
  nursing: [
    ["scrub_nurse_name", "Scrub nurse"],
    ["circulating_nurse_name", "Circulating nurse"],
    ["positioning", "Positioning"],
    ["skin_preparation", "Skin preparation"],
    ["diathermy_site", "Diathermy site"],
    ["tourniquet_details", "Tourniquet"],
    ["implants_used", "Implants"],
    ["specimens_sent", "Specimens sent"],
    ["disposals", "Disposals"],
    ["skin_condition", "Skin condition"],
    ["notes", "Notes"],
  ],
};

/** Rows for a note, dropping every field the writer left blank. */
function noteRows(note, spec) {
  if (!note) return [];
  return spec
    .map(([key, label]) => [label, note[key]])
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "");
}

/**
 * The operation as one signed document (OT_0078/0079).
 *
 * An uncertified note still prints — a case can be discussed before it is
 * signed off — but it is stamped DRAFT at the top of its section, because a
 * printed page loses every clue the screen gives about what is finished.
 */
export function printOperativeRecord(merged) {
  const h = merged?.header || {};
  const anyCertified = ["surgical", "anaesthesia", "nursing"].some(
    (k) => merged?.[k]?.certified_at,
  );

  const { doc, identity, y } = startDoc({
    title: "Operative Record",
    subtitle: `${h.primary_procedure || DASH}  ·  ${formatIstDate(h.date) || DASH}`,
    chipLabel: anyCertified ? "CERTIFIED" : "DRAFT",
  });

  let next = cardPair(doc, y, {
    title: "Patient",
    rows: [
      ["Name", h.patient_name],
      ["MRN", h.health_record_id],
      ["Age / sex", [h.age, h.gender].filter(Boolean).join(" / ")],
      ["Ward", h.ward_name],
      ["Case no.", h.case_number],
    ],
  }, {
    title: "Operation",
    rows: [
      ["Theatre", h.place],
      ["Type", h.case_type],
      ["Into theatre", formatIstDateTime(h.in_or_at)],
      ["Out of theatre", formatIstDateTime(h.out_of_or_at)],
      ["Surgeon", h.primary_surgeon_name],
      ["Anaesthetist", h.anaesthetist_name],
    ],
  });

  const team = merged?.team || [];
  if (team.length) {
    next = card(
      doc, SIDE_M, next + CARD_GAP, U, "Team",
      team.map((m) => [
        m.attendee_type_name || "Member",
        [m.member_name, m.grade].filter(Boolean).join(" · "),
      ]),
    );
  }

  ["surgical", "anaesthesia", "nursing"].forEach((kind) => {
    const note = merged?.[kind];
    const rows = noteRows(note, RECORD_SECTIONS[kind]);
    if (!rows.length) return;

    const title = `${kind} note${note?.certified_at ? "" : " — DRAFT"}`;
    const { height } = measureRows(doc, rows, U);
    next = ensureSpace(doc, next + CARD_GAP, height + 6, (d) =>
      drawContinuationHeader(d, identity, "Operative Record", h.case_number, null, true),
    );
    next = card(doc, SIDE_M, next, U, title, rows);

    if (note?.certified_at) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...C.faint);
      doc.text(
        `Certified ${formatIstDateTime(note.certified_at)} by ${note.certified_by_name || DASH}`,
        SIDE_M + CARD_PAD_X,
        next + 3.4,
      );
      next += 4;
    }
  });

  // The counts are the legal record that nothing was left inside the patient,
  // so a discrepancy prints as its own line rather than a column value.
  const counts = merged?.counts || [];
  if (counts.length) {
    const rows = counts.map((c) => [
      `${c.item_type || "Item"}${c.item_name ? ` — ${c.item_name}` : ""}`,
      [
        `initial ${c.initial_count ?? DASH}`,
        c.added_count ? `added ${c.added_count}` : null,
        `at closure ${c.closure_count ?? DASH} of ${c.expected_at_closure ?? DASH}`,
        c.result === "DISCREPANCY"
          ? `DISCREPANCY${c.discrepancy_reason ? ` — ${c.discrepancy_reason}` : ""}`
          : c.result,
      ]
        .filter(Boolean)
        .join(" · "),
    ]);
    const { height } = measureRows(doc, rows, U);
    next = ensureSpace(doc, next + CARD_GAP, height, (d) =>
      drawContinuationHeader(d, identity, "Operative Record", h.case_number, null, true),
    );
    next = card(doc, SIDE_M, next, U, "Counts", rows);
  }

  const consumption = merged?.consumption || [];
  if (consumption.length) {
    const rows = consumption.map((c) => [
      c.item_name || "Item",
      [c.quantity, c.unit].filter(Boolean).join(" "),
    ]);
    const { height } = measureRows(doc, rows, U);
    next = ensureSpace(doc, next + CARD_GAP, height, (d) =>
      drawContinuationHeader(d, identity, "Operative Record", h.case_number, null, true),
    );
    next = card(doc, SIDE_M, next, U, "Consumed", rows);
  }

  drawSignatureRow(doc, next + 6, "Surgeon", "Anaesthetist");

  stampFooters(doc, identity, true);
  open(doc);
}

// ── 4. Consent form ───────────────────────────────────────────────────────────

/**
 * The consent form, with the patient and the operation already on it.
 *
 * THE SYSTEM DOES NOT HOLD A SIGNED CONSENT to print — consent is recorded as a
 * decision, with a scan attached where one exists. What a theatre needs printed
 * is the FORM: the right patient, the right operation, the right side, so the
 * one thing that must not be wrong is not handwritten at the bedside.
 *
 * The declaration text is deliberately plain and short. It is a starting point a
 * hospital replaces with its own approved wording, not a legal instrument this
 * module invented.
 */
export function printConsentForm(otCase, procedureName) {
  const { doc, identity, y } = startDoc({
    title: "Consent for Operation",
    subtitle: "To be signed before the patient leaves the ward",
    chipLabel: (otCase.consent?.status || "PENDING").replace(/_/g, " "),
  });

  let next = cardPair(doc, y, {
    title: "Patient",
    rows: [
      ["Name", otCase.patient_name],
      ["MRN", otCase.health_record_id],
      ["Age / sex", [otCase.age, otCase.gender].filter(Boolean).join(" / ")],
      ["Ward", otCase.ward_name],
    ],
  }, {
    title: "Operation",
    rows: [
      ["Procedure", procedureName],
      ["Diagnosis", otCase.diagnosis],
      ["Surgeon", otCase.primary_surgeon_name],
      ["Case no.", otCase.case_number],
    ],
  });

  next = textCard(
    doc, SIDE_M, next + CARD_GAP, U, "Declaration",
    "I confirm that the nature and purpose of the operation named above, its " +
      "likely benefits, the risks involved and the alternatives to it have been " +
      "explained to me in a language I understand. I have had the opportunity to " +
      "ask questions and they have been answered.\n\n" +
      "I understand that additional procedures may become necessary during the " +
      "operation, and I consent to those that are required to save my life or to " +
      "prevent serious harm.\n\n" +
      "I consent to the administration of anaesthesia and to the disposal of any " +
      "tissue removed.",
  );

  // Side, spelt out, above the signatures — wrong-side surgery is the never-event
  // this form exists to prevent, and it must not be reachable by a tick alone.
  next += CARD_GAP;
  doc.setDrawColor(...C.navy);
  doc.setLineWidth(0.6);
  doc.rect(SIDE_M, next, U, 14, "S");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...C.navy);
  doc.text("SIDE / SITE (write in full — left, right, bilateral, not applicable):", SIDE_M + CARD_PAD_X, next + 5.5);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.2);
  doc.line(SIDE_M + CARD_PAD_X, next + 11, SIDE_M + U - CARD_PAD_X, next + 11);
  next += 14;

  drawSignatureRow(doc, next + 8, "Patient / next of kin (relationship)", "Date and time");
  drawSignatureRow(doc, next + 26, "Surgeon who explained the operation", "Witness / interpreter");

  stampFooters(doc, identity, true);
  open(doc);
}
